import { MeshPhongMaterial, Color, DoubleSide, CubeTexture, RGBFormat, FrontSide, BackSide } from "three";
import { RenderSide } from "../substrates/types";
import { compositeImages, colorCanvas, releaseCanvases, tileImage } from "./canvasUtility";
import { CompositeMode, MapMode } from "./renderingModes";
import { createTextureFromCanvas } from "./webGLHelper";
function sizeFromSurface(surface) {
  if (surface.trim) {
    return {
      width: surface.trim.bottomRightX - surface.trim.topLeftX,
      height: surface.trim.bottomRightY - surface.trim.topLeftY
    };
  }
  return surface.fullBleed;
}
function generateTiledImage(tiledTexture, textureSize, physicalSize, materialImages, materialCanvases) {
  const key = [tiledTexture.sourceUrl, tiledTexture.dpi, tiledTexture.overlayColor, textureSize.width, textureSize.height, physicalSize.width, physicalSize.height].join("");
  if (!materialCanvases[key]) {
    materialCanvases[key] = tileImage(tiledTexture, textureSize, physicalSize, materialImages);
  }
  return {
    key,
    canvas: materialCanvases[key]
  };
}
function generateTiledTexture(tiledTexture, textureSize, physicalSize, materialImages, materialCanvases, materialTextures) {
  const key = [tiledTexture.sourceUrl, tiledTexture.dpi, tiledTexture.overlayColor, textureSize.width, textureSize.height, physicalSize.width, physicalSize.height].join("");
  if (!materialCanvases[key]) {
    materialCanvases[key] = tileImage(tiledTexture, textureSize, physicalSize, materialImages);
  }
  if (!materialTextures[key]) {
    materialTextures[key] = createTextureFromCanvas(materialCanvases[key]);
  }
  return {
    key,
    texture: materialTextures[key]
  };
}
export function generateTextureFromImage(materialKey, materialImages, materialTextures) {
  if (!materialTextures[materialKey]) {
    materialTextures[materialKey] = createTextureFromCanvas(materialImages[materialKey]);
  }
  return materialTextures[materialKey];
}
function generateReflectionTexture(materialImages, materialTextures, environment, reflectionMapOverrideUrls) {
  const key = [environment.reflectionUrl, environment.reflectionUrls, reflectionMapOverrideUrls].join("");
  if (!materialTextures[key]) {
    const cubeTexture = new CubeTexture();
    cubeTexture.format = RGBFormat;
    let urls = reflectionMapOverrideUrls;
    // Use the overridden reflection map if provided, otherwise default to environment
    if (urls === undefined) {
      // If only one map is provided, use it 6 times. Otherwise use the array of 6 maps
      if (environment.reflectionUrl) {
        urls = new Array(6).fill(environment.reflectionUrl);
      } else {
        urls = environment.reflectionUrls;
      }
    }
    if (urls === undefined) {
      throw Error("No refection urls defined!");
    }
    for (let i = 0; i < 6; i++) {
      cubeTexture.images[i] = materialImages[urls[i]];
    }
    cubeTexture.needsUpdate = true;
    materialTextures[key] = cubeTexture;
  }
  return materialTextures[key];
}
function colorImage(color, mode, canvasKey, materialCanvases) {
  const key = "".concat(canvasKey, "-").concat(color.r, "-").concat(color.g, "-").concat(color.b, "-").concat(mode);
  if (materialCanvases[key]) {
    return {
      key,
      canvas: materialCanvases[key]
    };
  }
  const colorHex = "#".concat(color.getHexString());
  const baseCanvas = materialCanvases[canvasKey];
  // Fill the background of the base map with the substrate color
  if (mode === CompositeMode.normal) {
    const colorFill = colorCanvas({
      width: baseCanvas.width,
      height: baseCanvas.height
    }, colorHex);
    materialCanvases[key] = compositeImages(colorFill, baseCanvas, CompositeMode.normal, "tile");
    // Flush intermediate canvas
    releaseCanvases([colorFill]);
    return {
      key,
      canvas: materialCanvases[key]
    };
  }
  if (mode === CompositeMode.multiply) {
    // Skip base color compositing when the target color is already white
    if (color.r === 1 && color.g === 1 && color.b === 1) {
      return {
        key: canvasKey,
        canvas: baseCanvas
      };
    }
    // Multiply non-white the substrate color onto the tiled texture
    // ---------------------------------------------------
    // TODO in the future this should become a shader to correctly support
    // the multiply blending mode. HTML canvas multiply does not preserve opacity
    // so 'source-in' is currently used which doesn't preserve content and is
    // only used for transparent materials like plastics
    const colorFill = colorCanvas({
      width: baseCanvas.width,
      height: baseCanvas.height
    }, colorHex);
    materialCanvases[key] = compositeImages(baseCanvas, colorFill, CompositeMode.sourceIn, "tile");
    // Flush intermediate canvas
    releaseCanvases([colorFill]);
    return {
      key,
      canvas: materialCanvases[key]
    };
  }
  return {
    key: canvasKey,
    canvas: baseCanvas
  };
}
export function generateSubstrate(documentId, surface, textureSize, environment, materialImages, materialCanvases, materialTextures, documentCanvases, tempCanvases) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
  const physicalSize = sizeFromSurface(surface);
  const {
    substrate
  } = surface;
  const material = new MeshPhongMaterial({
    shininess: substrate.shininess,
    emissive: (_a = substrate.emissive) !== null && _a !== void 0 ? _a : 0x0,
    opacity: (_b = substrate.opacity) !== null && _b !== void 0 ? _b : 1.0,
    transparent: true
  });
  if (substrate.fullyTransparent) {
    return material;
  }
  // For semi-transparent materials disable depth writing to show occluded geometry
  // Ensure transparent materials are double sided so they are see-through
  if (substrate.supportsTransparency) {
    material.depthWrite = false;
    material.side = DoubleSide;
  }
  // Allow optional side overrides (used in transparent materials that are not completely see-through)
  if (substrate.side) {
    if (substrate.side === RenderSide.front) {
      material.side = FrontSide;
    } else if (substrate.side === RenderSide.back) {
      material.side = BackSide;
    } else {
      material.side = DoubleSide;
    }
  }
  // The base color material properties of the substrate
  const baseColor = new Color(substrate.color);
  const baseColorMode = (_c = substrate.baseColorMode) !== null && _c !== void 0 ? _c : CompositeMode.normal;
  // The substrate has a base texture (i.e. kraft or semi-transparent plastic)
  if (substrate.baseTexture) {
    // Generate the base image by tiling the base texture at it's correct DPI (based on physical size)
    const {
      key: tileKey
    } = generateTiledImage(substrate.baseTexture, textureSize, physicalSize, materialImages, materialCanvases);
    const {
      key: colorKey,
      canvas: coloredCanvas
    } = colorImage(baseColor, baseColorMode, tileKey, materialCanvases);
    // If a document exists, composite the document onto the base texture using it's desired blending mode
    if (documentId) {
      const documentKey = "".concat(documentId, "-").concat(colorKey, "-").concat((_d = substrate.overlayTexture) === null || _d === void 0 ? void 0 : _d.sourceUrl);
      if (!materialTextures[documentKey]) {
        const composite = compositeImages(coloredCanvas, documentCanvases[documentId], (_e = substrate.baseCompositeMode) !== null && _e !== void 0 ? _e : CompositeMode.normal, "tile");
        // Composite an overlay if required
        if (substrate.overlayTexture) {
          const overlayComposite = compositeImages(composite, materialImages[substrate.overlayTexture.sourceUrl], (_f = substrate.overlayTexture.compositeMode) !== null && _f !== void 0 ? _f : CompositeMode.normal, "stretch");
          tempCanvases[documentKey] = overlayComposite;
          materialTextures[documentKey] = createTextureFromCanvas(overlayComposite);
        } else {
          tempCanvases[documentKey] = composite;
          materialTextures[documentKey] = createTextureFromCanvas(composite);
        }
      }
      material.map = materialTextures[documentKey];
      // Otherwise composite an overlay if it exists onto the base texture material
    } else {
      const baseTextureKey = "".concat(colorKey, "-").concat((_g = substrate.overlayTexture) === null || _g === void 0 ? void 0 : _g.sourceUrl);
      // Generate the texture if it isn't in the cache
      if (!materialTextures[baseTextureKey]) {
        // Apply an overlay if required
        if (substrate.overlayTexture) {
          const overlayComposite = compositeImages(coloredCanvas, materialImages[substrate.overlayTexture.sourceUrl], (_h = substrate.overlayTexture.compositeMode) !== null && _h !== void 0 ? _h : CompositeMode.normal, "stretch");
          materialTextures[baseTextureKey] = createTextureFromCanvas(overlayComposite);
        } else {
          materialTextures[baseTextureKey] = createTextureFromCanvas(coloredCanvas);
        }
      }
      material.map = materialTextures[baseTextureKey];
    }
    // If no base texture exists, use a colored background and composite the document and overlay (if it exists)
  } else if (documentId) {
    const hexBaseColor = "#".concat(new Color(substrate.color).getHexString());
    const documentKey = "".concat(documentId, "-").concat(hexBaseColor, "-").concat((_j = substrate.overlayTexture) === null || _j === void 0 ? void 0 : _j.sourceUrl);
    if (!materialTextures[documentKey]) {
      const coloredCanvas = colorCanvas(textureSize, hexBaseColor);
      const composite = compositeImages(coloredCanvas, documentCanvases[documentId], (_k = substrate.baseCompositeMode) !== null && _k !== void 0 ? _k : CompositeMode.normal, "tile");
      // Composite an overlay if required
      if (substrate.overlayTexture) {
        const overlayComposite = compositeImages(composite, materialImages[substrate.overlayTexture.sourceUrl], (_l = substrate.overlayTexture.compositeMode) !== null && _l !== void 0 ? _l : CompositeMode.normal, "stretch");
        tempCanvases[documentKey] = overlayComposite;
        materialTextures[documentKey] = createTextureFromCanvas(overlayComposite);
      } else {
        tempCanvases[documentKey] = composite;
        materialTextures[documentKey] = createTextureFromCanvas(composite);
      }
      // Free the intermediate canvas
      releaseCanvases([coloredCanvas]);
    }
    material.map = materialTextures[documentKey];
  } else {
    material.color = baseColor;
    material.side = DoubleSide;
    // If an overlay is required, a real base color texture needs to be generated for compositing
    if (substrate.overlayTexture) {
      const hexBaseColor = "#".concat(new Color(substrate.color).getHexString());
      const overlayKey = "".concat(hexBaseColor, "-").concat((_m = substrate.overlayTexture) === null || _m === void 0 ? void 0 : _m.sourceUrl);
      if (!materialTextures[overlayKey]) {
        const coloredCanvas = colorCanvas(textureSize, hexBaseColor);
        const overlayComposite = compositeImages(coloredCanvas, materialImages[substrate.overlayTexture.sourceUrl], (_o = substrate.overlayTexture.compositeMode) !== null && _o !== void 0 ? _o : CompositeMode.normal, "stretch");
        materialTextures[overlayKey] = createTextureFromCanvas(overlayComposite);
        // Free the intermediate canvas
        releaseCanvases([coloredCanvas]);
      }
      material.map = materialTextures[overlayKey];
    }
  }
  // Apply reflectivity if the substrate requests
  if (substrate.reflectivity) {
    material.reflectivity = substrate.reflectivity;
    material.envMap = generateReflectionTexture(materialImages, materialTextures, environment, substrate.reflectionMapOverrideUrls);
  }
  // Add a bump map if it exists
  if (substrate.bumpTexture && substrate.bumpScale) {
    material.bumpScale = substrate.bumpScale;
    material.bumpMap = generateTiledTexture(substrate.bumpTexture, textureSize, physicalSize, materialImages, materialCanvases, materialTextures).texture;
  }
  // Add a specular map if it exists
  if (substrate.specularTexture) {
    material.specularMap = generateTiledTexture(substrate.specularTexture, textureSize, physicalSize, materialImages, materialCanvases, materialTextures).texture;
  }
  // Add an alpha map if it exists
  if (substrate.alphaTexture) {
    material.alphaMap = generateTiledTexture(substrate.alphaTexture, textureSize, physicalSize, materialImages, materialCanvases, materialTextures).texture;
  }
  return material;
}
export function generateFinish(documentId, finishId, finish, surface, textureSize, environment, materialImages, materialCanvases, materialTextures, documentCanvases, tempCanvases) {
  const physicalSize = sizeFromSurface(surface);
  // Material that will be further customized per finish
  const material = new MeshPhongMaterial({
    transparent: true,
    shininess: finish.shininess
  });
  // The finish requires an environment map reflection cube
  if (finish.reflectivity) {
    material.reflectivity = finish.reflectivity;
    material.envMap = generateReflectionTexture(materialImages, materialTextures, environment, finish.reflectionMapOverrideUrls);
  }
  const finishMapKey = "".concat(documentId, "-").concat(finishId, "-map");
  if (!materialTextures[finishMapKey]) {
    // Initialize the base texture to the document
    let baseTexture = documentCanvases[documentId];
    // If the finish has a base texture, load that and composite it (ex. metallic)
    if (finish.baseTexture) {
      baseTexture = generateTiledImage(finish.baseTexture, textureSize, physicalSize, materialImages, materialCanvases).canvas;
      // If the compositing mode is not normal, the real document texture must be pulled for blending
      if (finish.baseCompositeMode !== CompositeMode.normal) {
        tempCanvases[finishMapKey] = compositeImages(baseTexture, documentCanvases[documentId], finish.baseCompositeMode, "tile");
        baseTexture = tempCanvases[finishMapKey];
      }
    }
    tempCanvases[finishMapKey] = compositeImages(baseTexture, documentCanvases[finishId], CompositeMode.mask, "tile");
    materialTextures[finishMapKey] = createTextureFromCanvas(tempCanvases[finishMapKey]);
  }
  material.map = materialTextures[finishMapKey];
  material.bumpScale = finish.bumpScale;
  // Generate a bump map if necessary, otherwise inherit it
  if (finish.bumpMode === MapMode.generate) {
    const finishBumpKey = "".concat(finishId, "-bump");
    if (!materialTextures[finishBumpKey]) {
      materialTextures[finishBumpKey] = createTextureFromCanvas(documentCanvases[finishId]);
    }
    material.bumpMap = materialTextures[finishBumpKey];
  } else {
    material.bumpMap = materialTextures[finishMapKey];
  }
  // Apply a specular map if the substrate requests it
  if (finish.specularTexture) {
    material.specularMap = generateTiledTexture(finish.specularTexture, textureSize, physicalSize, materialImages, materialCanvases, materialTextures).texture;
  }
  // Add an alpha map if it exists
  if (finish.alphaTexture) {
    material.alphaMap = generateTiledTexture(finish.alphaTexture, textureSize, physicalSize, materialImages, materialCanvases, materialTextures).texture;
  }
  return material;
}